// Scats — POT side game, ported EXACTLY from GolfGamesScorecard's utils/scats.ts.
// Add-on that attaches to the main game and settles ONCE at the end (never per
// hole). Self-contained (strokes via ggHandicap sibling; no live imports).
//
// Rules (verbatim):
//   • Every opted-in player antes `buyIn` once → pot = buyIn × playerCount.
//   • A "scat" = a hole where a player's NET score is under par (birdie or
//     better; eagles count as one, no tiering).
//   • At end: share[i] = pot × scats[i] / totalScats, rounded to whole dollars
//     via largest-remainder (Hamilton) so integer shares sum exactly to the pot.
//   • totalScats === 0 → split equally: everyone gets their buy-in back (net 0).
//   • nets[i] = shares[i] − buyIn (signed, whole dollars; sum of nets === 0).
import { strokesPerHoleForMode } from './ggHandicap.js'

// Largest-remainder (Hamilton) rounding — verbatim from GGS.
function hamiltonRound(floatShares, target) {
  const floors = floatShares.map((v) => Math.floor(v))
  const remainders = floatShares.map((v, i) => ({ i, frac: v - floors[i] }))
  let residual = target - floors.reduce((s, v) => s + v, 0)
  if (residual <= 0) return floors.slice()
  remainders.sort((a, b) => (b.frac - a.frac) || (a.i - b.i))
  const out = floors.slice()
  for (let k = 0; k < remainders.length && residual > 0; k++) { out[remainders[k].i] += 1; residual -= 1 }
  return out
}

// Settle a Scats pot from per-player scat counts — verbatim from GGS.
export function settleScats(scatsByPlayer, buyIn) {
  const n = scatsByPlayer.length
  const intBuyIn = Math.max(0, Math.round(buyIn))
  const pot = intBuyIn * n
  const totalScats = scatsByPlayer.reduce((s, v) => s + v, 0)
  if (n === 0 || pot === 0) return { shares: Array(n).fill(0), nets: Array(n).fill(0), buyIn: intBuyIn, pot, totalScats }
  if (totalScats === 0) return { shares: Array(n).fill(intBuyIn), nets: Array(n).fill(0), buyIn: intBuyIn, pot, totalScats }
  const floatShares = scatsByPlayer.map((c) => pot * (c / totalScats))
  const shares = hamiltonRound(floatShares, pot)
  const nets = shares.map((s) => s - intBuyIn)
  return { shares, nets, buyIn: intBuyIn, pot, totalScats }
}

// SINGLE-WINNER scat rule: on each hole, the OUTRIGHT lowest net that is under
// par wins one scat. A tie for the low → no scat that hole. (Net uses the chosen
// stroke-allocation mode when useHandicap is on.)
//
// NOTE for iOS: the GolfGamesScorecard app currently awards a scat to EVERY
// birdie (scorecard.tsx:1289-1312, scats.ts), NOT a single winner. Per the
// "build web correct" rule this web implements single-winner; log the iOS fix.
// Full per-hole scat status: the OUTRIGHT single under-par winner, or the set of
// players TIED for the low under par (2+ → NOBODY wins). Returns { winnerId, tied }.
// Per-player handicap strokes on ONE hole under the scat's allocation mode.
// SINGLE SOURCE OF TRUTH: both scatHoleInfo (the net used to award scats) and the
// live scorecard's stroke dot read this, so the dot shown can never disagree with
// the net that decides the scat. Gross (useHandicap off) → everyone 0, no dots.
export function scatStrokesForHole(players, hole, { useHandicap = false, mode = 'cap1', strokeIndex } = {}) {
  if (!useHandicap) return players.map(() => 0)
  const field = players.map((p) => ({ courseHandicap: p.courseHandicap || 0 }))
  return players.map((_, pi) => strokesPerHoleForMode(mode, field[pi], field, strokeIndex)[hole - 1] || 0)
}

export function scatHoleInfo(players, scoresByHole, pars, hole, opts = {}) {
  const par = pars[hole - 1] ?? 4
  const strokes = scatStrokesForHole(players, hole, opts)
  const nets = players.map((p, pi) => {
    const g = Number(scoresByHole?.[hole]?.[p.id])
    if (!Number.isFinite(g) || g <= 0) return null
    return g - strokes[pi]
  })
  const entered = nets.map((netv, i) => ({ netv, i })).filter((x) => x.netv != null)
  if (!entered.length) return { winnerId: null, tied: [] }
  const min = Math.min(...entered.map((x) => x.netv))
  if (min >= par) return { winnerId: null, tied: [] } // nobody under par
  const low = entered.filter((x) => x.netv === min)
  if (low.length === 1) return { winnerId: players[low[0].i].id, tied: [] } // outright winner
  return { winnerId: null, tied: low.map((x) => players[x.i].id) } // TIE → no scat
}

// One scat to the outright single under-par winner; tie → null.
export function scatWinnerForHole(players, scoresByHole, pars, hole, opts = {}) {
  return scatHoleInfo(players, scoresByHole, pars, hole, opts).winnerId
}

// Per-player scat counts (array in player order) — one scat per hole to the
// single outright winner.
export function countScats(players, scoresByHole, pars, opts = {}) {
  const counts = players.map(() => 0)
  for (let h = 1; h <= 18; h++) {
    const winner = scatWinnerForHole(players, scoresByHole, pars, h, opts)
    if (winner) counts[players.findIndex((p) => p.id === winner)] += 1
  }
  return counts
}

// Full add-on settlement over the OPTED-IN players only.
export function settleScatsGame(allPlayers, scoresByHole, pars, cfg = {}) {
  const { buyIn = 10, useHandicap = false, mode = 'cap1', strokeIndex, optedIn } = cfg
  const inSet = optedIn ? new Set(optedIn) : new Set((allPlayers || []).map((p) => p.id))
  const players = (allPlayers || []).filter((p) => inSet.has(p.id))
  const scatsBy = countScats(players, scoresByHole, pars, { useHandicap, mode, strokeIndex })
  const payout = settleScats(scatsBy, buyIn)
  const rows = players
    .map((p, i) => ({ id: p.id, name: p.name, scats: scatsBy[i], share: payout.shares[i], net: payout.nets[i] }))
    .sort((a, b) => b.net - a.net || b.scats - a.scats)
  return { rows, pot: payout.pot, buyIn: payout.buyIn, totalScats: payout.totalScats, playerCount: players.length }
}
